/**
 * The Projects page's remembered view — filters, sort, grouping, table-vs-list.
 *
 * Feedback 04f37df4 (Lauren Webb): the page dropped every filter on unmount, so the team
 * rebuilt their view on every visit. The last-used view is written to localStorage on change
 * and restored on mount. Named presets (FilterPresetBar) stay a separate, explicit thing —
 * this is the implicit "where I left off" memory.
 *
 * Everything read back is validated rather than trusted. The blob may have been written by an
 * older (or newer) build, or hand-edited in devtools, and there is no UI to clear it — so an
 * unrecognized value has to degrade to the default instead of wedging the page. One case is a
 * hard crash rather than cosmetic: a filterGroups entry missing its `rules` array throws on the
 * first `g.rules.some(...)` during render.
 */

export type FilterField =
  | "status"
  | "assignee"
  | "current_assignee"
  | "client"
  | "type"
  | "year"
  | "month"
  | "tag"
  | "due_date"
  | "recurring"
  | "client_group";

export type FilterOperator = "is" | "is_not" | "is_empty" | "is_not_empty";

export type FilterRule = { field: FilterField; operator: FilterOperator; values: string[] };
export type FilterGroup = { combinator: "AND" | "OR"; rules: FilterRule[] };

export type SortField = "due_date" | "client" | "title" | "type" | "year" | "status" | "start_date";
export type ProjectsSortRule = { field: SortField; dir: "asc" | "desc" };
export type GroupByPrimary = "client" | "status" | "type" | "assignee" | "current_assignee" | "year" | "period";
export type GroupBySecondary = "none" | "status" | "type" | "assignee" | "current_assignee" | "year" | "period";

// col is a bare string to prevent UI imports
export type StoredColSort = { col: string | null; dir: "asc" | "desc" };

export interface StoredProjectsView {
  projectStatusFilter?: "active" | "all";
  hideFuture?: boolean;
  myWorkOnly?: boolean;
  viewMode?: "table" | "list";
  groupBy?: GroupByPrimary;
  groupBy2?: GroupBySecondary;
  sortRules?: ProjectsSortRule[];
  filterGroups?: FilterGroup[];
  colSort?: StoredColSort;
}

export const DEFAULT_SORT_RULES: ProjectsSortRule[] = [{ field: "due_date", dir: "asc" }];
export const DEFAULT_GROUP_BY: GroupByPrimary = "client";
export const DEFAULT_GROUP_BY_2: GroupBySecondary = "none";
export const DEFAULT_COL_SORT: StoredColSort = { col: null, dir: "asc" };

export const PROJECTS_VIEW_KEY = "centcom_projects_view";
// read s a fallback
export const LEGACY_VIEW_MODE_KEY = "projects.viewMode";

const GROUP_BY_PRIMARY_VALUES: GroupByPrimary[] = ["client", "status", "type", "assignee", "current_assignee", "year", "period"];
const GROUP_BY_SECONDARY_VALUES: GroupBySecondary[] = ["none", "status", "type", "assignee", "current_assignee", "year", "period"];
const SORT_FIELD_VALUES: SortField[] = ["due_date", "client", "title", "type", "year", "status", "start_date"];

// checks whether a rule actually narrows the result set
export function isRuleActive(rule: { operator?: string; values?: unknown[] } | null | undefined): boolean {
  if (!rule) return false;
  if (rule.operator === "is_empty" || rule.operator === "is_not_empty") return true;
  return Array.isArray(rule.values) && rule.values.length > 0;
}

// true when any group holds a rule that filters
export function hasActiveRules(groups: FilterGroup[]): boolean {
  return groups.some((g) => g.rules.some(isRuleActive));
}

// how many rules are actually filtered
export function countActiveRules(groups: FilterGroup[]): number {
  return groups.reduce((n, g) => n + g.rules.filter(isRuleActive).length, 0);
}

// validate the filter groups from any source
export function sanitizeFilterGroups(raw: unknown): FilterGroup[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const groups = raw
    .filter((g: unknown): g is FilterGroup => !!g && typeof g === "object" && Array.isArray((g as FilterGroup).rules))
    .map((g) => ({
      combinator: g.combinator === "OR" ? ("OR" as const) : ("AND" as const),
      // unrecognized rules are harmless, but a missing values in an array is not
      rules: g.rules.filter(
        (r: unknown): r is FilterRule =>
          !!r && typeof r === "object" &&
          typeof (r as FilterRule).field === "string" &&
          Array.isArray((r as FilterRule).values)
      ),
    }));
  return groups.length ? groups : undefined;
}

export function sanitizeStoredView(raw: unknown): StoredProjectsView {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const v = raw as Record<string, unknown>;
  const view: StoredProjectsView = {};

  if (v.projectStatusFilter === "active" || v.projectStatusFilter === "all") view.projectStatusFilter = v.projectStatusFilter;
  if (typeof v.hideFuture === "boolean") view.hideFuture = v.hideFuture;
  if (typeof v.myWorkOnly === "boolean") view.myWorkOnly = v.myWorkOnly;
  if (v.viewMode === "table" || v.viewMode === "list") view.viewMode = v.viewMode;
  if (GROUP_BY_PRIMARY_VALUES.includes(v.groupBy as GroupByPrimary)) view.groupBy = v.groupBy as GroupByPrimary;
  if (GROUP_BY_SECONDARY_VALUES.includes(v.groupBy2 as GroupBySecondary)) view.groupBy2 = v.groupBy2 as GroupBySecondary;

  if (Array.isArray(v.sortRules)) {
    const rules = v.sortRules.filter(
      (r: unknown): r is ProjectsSortRule =>
        !!r && typeof r === "object" &&
        SORT_FIELD_VALUES.includes((r as ProjectsSortRule).field) &&
        ((r as ProjectsSortRule).dir === "asc" || (r as ProjectsSortRule).dir === "desc")
    );
    if (rules.length) view.sortRules = rules;
  }

  const filterGroups = sanitizeFilterGroups(v.filterGroups);
  if (filterGroups) view.filterGroups = filterGroups;

  const cs = v.colSort as Partial<StoredColSort> | undefined;
  if (cs && typeof cs === "object" && (cs.dir === "asc" || cs.dir === "desc") && (cs.col === null || typeof cs.col === "string")) {
    view.colSort = { col: cs.col ?? null, dir: cs.dir };
  }

  return view;
}

export function loadPersistedView(): StoredProjectsView {
  let raw: unknown;
  try {
    const stored = localStorage.getItem(PROJECTS_VIEW_KEY);
    if (!stored) return {};
    raw = JSON.parse(stored);
  } catch {
    // corrupt json or storage unavaiblable
    return {};
  }
  return sanitizeStoredView(raw);
}

export function loadLegacyViewMode(): "table" | "list" | null {
  try {
    const v = localStorage.getItem(LEGACY_VIEW_MODE_KEY);
    return v === "table" || v === "list" ? v : null;
  } catch {
    return null;
  }
}

export function serializeView(view: StoredProjectsView): string | null {
  try {
    return JSON.stringify(view);
  } catch {
    return null;
  }
}

export function savePersistedView(json: string): void {
  try {
    localStorage.setItem(PROJECTS_VIEW_KEY, json);
  } catch {
    // quota exceeded, just do nothing
  }
}
